import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /learn/saint-tms-utah
 * As of November 2026, Magnus Medical's provider locator lists no verified
 * SAINT® provider in Utah. Honest about regional 5-day programs vs SAINT®.
 */
const faqs: LearnFAQ[] = [
  {
    question: "Is SAINT TMS available in Utah?",
    answer:
      "As of November 2026, Magnus Medical\u2019s provider locator lists no verified SAINT\u00ae provider in Utah. Some clinics in the region offer 5-day \u201caccelerated TMS,\u201d but those are separate, off-label programs, not SAINT\u00ae. Your options include a verified provider in another state or Neuro Wellness in Coral Springs, Florida, through our one-week travel program.",
  },
  {
    question: "Is accelerated TMS in Utah the same as SAINT?",
    answer:
      "No. Three things separate SAINT\u00ae from a copied 5-day schedule: the personalized brain-MRI target, the FDA-cleared targeting software and Magnus device, and the published trial results (79% remission). A regional 5-day program without those is a different, off-label treatment.",
  },
];

export const pageSaintTmsUtah = makeArticle({
  slug: "saint-tms-utah",
  title:
    "SAINT\u00ae & Accelerated TMS in Utah: Verified Options, Cost & Travel (2026)",
  description:
    "Looking for SAINT\u00ae or 5-day accelerated TMS in Utah? As of November 2026 there\u2019s no verified SAINT\u00ae provider in the state. Here are your real options.",
  canonical: "/learn/saint-tms-utah",
  h1: "SAINT\u00ae TMS for Utah Residents",
  breadcrumbLabel: "SAINT\u00ae TMS in Utah",
  shortAnswer:
    "As of November 2026, Magnus Medical\u2019s provider locator lists no verified SAINT\u00ae provider in Utah. Some clinics in the region offer 5-day \u201caccelerated TMS,\u201d but those are separate, off-label programs, not SAINT\u00ae. Your options include a verified SAINT\u00ae provider in another state, or Neuro Wellness in Coral Springs, Florida, through our one-week travel program.",
  keyTakeaways: [
    "No verified SAINT\u00ae provider in Utah as of November 2026 \u2014 check the Magnus locator before booking.",
    "Regional 5-day \u201caccelerated TMS\u201d programs are off-label, not SAINT\u00ae.",
    "From Salt Lake City (SLC), Fort Lauderdale (FLL) is the closest major South Florida airport.",
    "The first call is an eligibility screening; the medical evaluation happens in Florida.",
    "We run a free insurance benefits check and offer self-pay from $25,000.",
  ],
  sections: [
    {
      heading: "Is SAINT TMS available in Utah?",
      paragraphs: [
        "As of November 2026, Magnus Medical\u2019s official provider locator lists no verified SAINT\u00ae provider in Utah. Some clinics in the region offer 5-day \u201caccelerated TMS\u201d programs. Those are separate, off-label programs \u2014 not SAINT\u00ae \u2014 and their own providers describe them as untested in the SAINT\u00ae clinical trials.",
        "Your options include a verified SAINT\u00ae provider in a neighboring state, or Neuro Wellness TMS Centers of America in Coral Springs, Florida, through our one-week travel program. [[See how the travel program works|/learn/travel-for-saint-tms]].",
      ],
    },
    {
      heading: "Is accelerated TMS in Utah the same as SAINT?",
      paragraphs: [
        "No. Three things separate SAINT\u00ae from a copied 5-day schedule: (1) a personalized target chosen from your own brain MRI, (2) FDA-cleared targeting software on the Magnus Medical system, and (3) the published trial results \u2014 79% remission in the 2022 controlled trial (Cole et al., Am J Psychiatry 2022). A program that skips the scan or uses head measurements on a standard machine is a different treatment. [[BEAM F3 vs fMRI targeting|/learn/beam-f3-vs-fmri-tms]].",
      ],
    },
    {
      heading: "Getting to Coral Springs from Utah",
      paragraphs: [
        "From Salt Lake City International (SLC), Fort Lauderdale\u2013Hollywood International (FLL) is the closest major airport to our Coral Springs clinic \u2014 about 30 minutes away by car. Palm Beach International (PBI) is the next closest. Flight times and nonstop availability change, so check current routes when you plan; we\u2019ll help coordinate scheduling around your travel.",
        "Because SAINT\u00ae is five consecutive days, Utah patients typically fly in for a focused week rather than commuting. We schedule the evaluation and MRI to keep your trip as short as practical \u2014 confirm the exact number of trips with us when you book.",
      ],
    },
    {
      heading: "Insurance and cost from Utah",
      paragraphs: [
        "SAINT\u00ae coverage is currently limited and decided case by case through prior authorization. We run a free insurance benefits check on your plan \u2014 we never say \u201ccovered by insurance\u201d about SAINT\u00ae. Self-pay is available from $25,000 for the full five-day course, including the brain MRI and all 50 sessions.",
      ],
    },
    {
      heading: "How your consultation works from Utah",
      paragraphs: [
        "Your first call is an eligibility screening to confirm SAINT\u00ae may be appropriate and that a brain MRI is safe for you. The full medical evaluation happens in Florida at our Coral Springs clinic. Our clinicians may not be licensed to provide telehealth in Utah, so we won\u2019t promise a telehealth evaluation \u2014 the psychiatric evaluation is in person.",
        "South Florida\u2019s winter weeks are mild, which makes a treatment week in November through March comfortable. Pack light layers and plan for mostly indoor clinic days. Call 954-827-9793 or [[request a consultation|/contactus]] to start.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification \u2014 Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: [
    {
      title: "Traveling for SAINT\u00ae TMS",
      href: "/learn/travel-for-saint-tms",
      description: "How the one-week Florida travel program works",
    },
    {
      title: "Local TMS vs Traveling for 5-Day TMS",
      href: "/learn/local-tms-vs-traveling-for-5-day-tms",
      description: "How to decide what\u2019s right for you",
    },
    {
      title: "What Is SAINT\u00ae TMS?",
      href: "/learn/what-is-saint-tms",
      description: "FDA clearance, protocol, and evidence",
    },
  ] as RL[],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  safetyBlock:
    "SAINT\u00ae is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT\u00ae is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. Neuro Wellness TMS Centers of America does not name or endorse any competitor. Individual results vary. No outcome is guaranteed. Coverage varies by plan.",
  ctaBand: {
    headline: "See if SAINT\u00ae is a fit, free",
    primaryText: "Check if I may be a candidate",
    primaryHref: "/am-i-a-candidate",
    secondaryText: "Free insurance benefits check",
    secondaryHref: "/contactus",
    phone: "954-827-9793",
  },
});
